// /domains/tms/store/shipments/shipmentsStore.ts
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer'
import { client } from '../graphql/client';
import { WarehouseType, WAREHOUSE_FIELDS } from '../graphql/types/WarehouseType';
import { ApolloError } from '@apollo/client';
import { gql } from '@apollo/client';
import { RoleType } from 'graphql/types/AccountType';
import { SiteType } from 'graphql/types/SiteType';

export interface WarehouseState {
    // 服务器数据
    warehouses: WarehouseType[];
    currentWarehouse: WarehouseType | null;
    isLoading: boolean;
    error?: ApolloError;
    setCurrentWarehouse: (warehouseId: number) => void;
    fetchWarehouses: (role: RoleType, siteType?: SiteType) => Promise<void>;
}

const GET_WAREHOUSE_LIST_FOR_DSP_MANAGER = gql`
    query GetWarehouseListForDspManager {
        warehouseList: getWarehouseListForDspManager {
            ...WarehouseFields
        }
    }
    ${WAREHOUSE_FIELDS}
`;

const GET_WAREHOUSE_LIST_FOR_HEADQUARTERS_STAFF = gql`
    query GetWarehouseListForHeadquarters {
        warehouseList: getWarehouseListForHeadquarters {
            ...WarehouseFields
        }
    }
    ${WAREHOUSE_FIELDS}
`;

const GET_ACCOUNT_BIND_WAREHOUSE = gql`
    query GetAccountBindWarehouse {
        warehouse: getAccountBindWarehouse {
            ...WarehouseFields
        }
    }
    ${WAREHOUSE_FIELDS}
`;

interface WarehouseListResponse {
    warehouseList: WarehouseType[];
}

interface WarehouseResponse {
    warehouse: WarehouseType;
}

const getCurrentWarehouseId = () => {
    return parseInt(localStorage.getItem('currentWarehouseId') || '0');
}

const setCurrentWarehouseId = (warehouseId: number) => {
    localStorage.setItem('currentWarehouseId', warehouseId.toString());
}


const getWarehousesByRole = async (role: RoleType): Promise<{ warehouses: WarehouseType[], error: ApolloError | null }> => {
    const getWarehousesForWmsManager = async () => {
        const { data, error } = await client.query<WarehouseResponse>({ query: GET_ACCOUNT_BIND_WAREHOUSE });
        return { warehouses: data?.warehouse ? [data.warehouse] : [], error: error || null };
    }

    const getWarehousesForDspManager = async () => {
        const { data, error } = await client.query<WarehouseListResponse>({ query: GET_WAREHOUSE_LIST_FOR_DSP_MANAGER });
        return { warehouses: data?.warehouseList || [], error: error || null };
    }

    const getWarehousesForHeadquartersStaff = async () => {
        const { data, error } = await client.query<WarehouseListResponse>({ query: GET_WAREHOUSE_LIST_FOR_HEADQUARTERS_STAFF });
        return { warehouses: data?.warehouseList || [], error: error || null };
    }

    const getWarehousesForPickupDispatcher = async () => {
        const { data, error } = await client.query<WarehouseResponse>({ query: GET_ACCOUNT_BIND_WAREHOUSE });
        return { warehouses: data?.warehouse ? [data.warehouse] : [], error: error || null };
    }

    const getWarehousesForFinancialTeam = async () => {
        const { data, error } = await client.query<WarehouseListResponse>({ query: GET_WAREHOUSE_LIST_FOR_HEADQUARTERS_STAFF });
        return { warehouses: data?.warehouseList || [], error: error || null };
    }

    const getWarehousesForTmsDispatcher = async () => {
        const { data, error } = await client.query<WarehouseResponse>({ query: GET_ACCOUNT_BIND_WAREHOUSE });
        return { warehouses: data?.warehouse ? [data.warehouse] : [], error: error || null };
    }
    switch (role) {
        case 'WAREHOUSE_MANAGER': 
            return getWarehousesForWmsManager();
        case 'HUB_MANAGER':
            return getWarehousesForWmsManager();
        case 'DSP_MANAGER':
            return getWarehousesForDspManager();
        case 'HEADQUARTERS_STAFF':
            return getWarehousesForHeadquartersStaff();
        case 'PICKUP_DISPATCHER':
            return getWarehousesForPickupDispatcher();
        case 'FINANCIAL_TEAM':
            return getWarehousesForFinancialTeam();
        case 'TMS_DISPATCHER':
            return getWarehousesForTmsDispatcher();
        default:
            throw new Error(`Unsupported role: ${role}`);
    }
}

export const useWarehouseStore = create<WarehouseState>()(
    immer((set, get) => ({
        // 默认状态
        warehouses: [],
        currentWarehouse: null,
        isLoading: false,
        error: undefined,

        fetchWarehouses: async (role: RoleType, siteType?: SiteType) => {
            if (get().warehouses.length > 0 && get().currentWarehouse) {
                return;
            }
            
            try {
                set({ isLoading: true, error: undefined });

                let { warehouses, error } = await getWarehousesByRole(role);

                if (siteType) {
                    warehouses = warehouses.filter(warehouse => warehouse.siteType === siteType);
                }

                if (warehouses.length > 0) {
                    set({ warehouses });

                    const currentWarehouseId = getCurrentWarehouseId();
                    const currentWarehouse = warehouses.find(warehouse => warehouse.id == currentWarehouseId);
                    if (currentWarehouse) {
                        get().setCurrentWarehouse(currentWarehouse.id!);
                        setCurrentWarehouseId(currentWarehouse.id!);
                    } else {
                        get().setCurrentWarehouse(warehouses[0].id!);
                        setCurrentWarehouseId(warehouses[0].id!);
                    }
                }

                set({ error: error || undefined, isLoading: false });

            } catch (error) {
                console.error("fetchWarehouses error", error);
                set({ error: error as ApolloError, isLoading: false });
            }
        },
        setCurrentWarehouse: (warehouseId: number) => {
            const toWarehouse = get().warehouses.find((warehouse) => warehouse.id == warehouseId);
            set({ currentWarehouse: toWarehouse });
            setCurrentWarehouseId(warehouseId);
        },
    }),
    )
);


export const useCurrentWarehouse = () => {
    return useWarehouseStore(state => state.currentWarehouse);
};